import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "src/common/axiosInstance";

export interface FetchTabDataPayload {
  /** Composite cache key, e.g. "spotify:creative". */
  cacheKey:      string;
  /** API endpoint for this tab, e.g. "/exchange-creative-data". */
  endpoint:      string;
  /** Query params already assembled by the tab's registry entry plus pagination/sort. */
  requestParams: Record<string, unknown>;
  /** Deterministic signature of requestParams — used for cache validation. */
  fetchKey:      string;
  /** Signature of the filter-only params (no page/sort) — used for staleness detection. */
  queryIdentity: string;
}

export interface FetchTabDataResult {
  cacheKey:      string;
  fetchKey:      string;
  queryIdentity: string;
  rows:          Record<string, unknown>[];
  pagination: {
    totalRecords: number;
    totalPages:   number;
    currentPage:  number;
    perPage:      number;
  };
  /** Metrics the user is permitted to customize. From API response. */
  permittedMetrics: string[];
}

export interface FetchTabDataRejection {
  cacheKey: string;
  message:  string;
}

export const fetchTabData = createAsyncThunk(
  "tabData/fetchTabData",
  async (payload: FetchTabDataPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(payload.endpoint, payload.requestParams);

      const result: FetchTabDataResult = {
        cacheKey:      payload.cacheKey,
        fetchKey:      payload.fetchKey,
        queryIdentity: payload.queryIdentity,
        rows:          response.data.data ?? [],
        pagination: {
          totalRecords: response.data.pagination?.total_records ?? 0,
          totalPages:   response.data.pagination?.total_pages   ?? 1,
          currentPage:  response.data.pagination?.current_page  ?? 1,
          perPage:      response.data.pagination?.per_page       ?? 10,
        },
        permittedMetrics: response.data.permitted_metrics ?? [],
      };
      return result;
    } catch (error: any) {
      const rejection: FetchTabDataRejection = {
        cacheKey: payload.cacheKey,
        message:
          error.response?.data?.message ??
          error.message ??
          "Failed to load tab data",
      };
      return rejectWithValue(rejection);
    }
  }
);
